import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Sidebar from './Sidebar';
import Header from './Header';
import { Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useMerchantAuth } from '../../../contexts/AuthContext';
import NoAccessPage from '../../../features/auth/pages/NoAccessPage';
import TutorialOverlay from '../../../features/tutorial/components/TutorialOverlay';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { createLogger } from '../../../shared/config/logger';
import { resetTutorial, startTutorial, tutorialSliceAPI } from '../../../features/tutorial/store/tutorialSlice';
import { useAppSelector } from '../../../shared/store';

const logger = createLogger('DashboardLayout');

/**
 * One-shot tutorial auto-start for merchants with dashboard access only.
 */
function DashboardTutorialAutoStart({ userId, dispatch }) {
    const { pathname } = useLocation();
    const tutorialState = useAppSelector((state) => state.tutorial);
    const hasChecked = React.useRef(false);

    useEffect(() => {
        if (hasChecked.current || !userId) {
            return undefined;
        }
        if (!pathname.startsWith('/dashboard')) {
            return undefined;
        }
        hasChecked.current = true;

        let cancelled = false;
        const timer = setTimeout(async () => {
            const { isActive, isComplete } = tutorialState;
            try {
                const result = await dispatch(tutorialSliceAPI.fetchTutorialStatus()).unwrap();
                if (cancelled) {
                    return;
                }
                const dbTutorialCompleted = result.tutorial_completed ?? false;
                if (!isActive && !dbTutorialCompleted) {
                    dispatch(startTutorial());
                }
            } catch (error) {
                if (cancelled) {
                    return;
                }
                logger.error('Failed to fetch tutorial status', { error });
                if (!isActive && !isComplete) {
                    dispatch(startTutorial());
                }
            }
        }, 500);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [userId, pathname, dispatch, tutorialState.isActive, tutorialState.isComplete]);

    return null;
}

function DashboardContent() {
    const { loading, noAccess } = useMerchantAuth();
    const { state } = useAuth();
    const dispatch = useDispatch();
    const userId = state.userId;

    useEffect(() => {
        if (noAccess) {
            dispatch(resetTutorial());
        }
    }, [noAccess, dispatch]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
        );
    }

    if (noAccess) {
        return <NoAccessPage />;
    }

    return (
        <>
            <TutorialOverlay />
            <DashboardTutorialAutoStart userId={userId} dispatch={dispatch} />
            <div className="flex min-h-screen bg-gray-100">
                <Sidebar />
                <div className="flex-1">
                    <Header />
                    <Outlet />
                </div>
            </div>
        </>
    );
}

export default function Dashboard() {
    return (
        <AuthProvider>
            <DashboardContent />
        </AuthProvider>
    );
}
